import { createFileRoute } from '@tanstack/react-router';

import { pageHead } from '../lib/seo';
import { canonical } from '../lib/site';
import { LEGAL, orBlank } from '../lib/legal';
import { PageContainer } from '../components/layout/page-container';
import { PageHeader } from '../components/ui/page-header';
import { Article, LegalDoc, List } from '../features/legal/legal-doc';

const TERMS_DESCRIPTION = '좋소 과수원 서비스 이용약관.';

export const Route = createFileRoute('/terms')({
  head: () => pageHead({ title: '이용약관', description: TERMS_DESCRIPTION, url: canonical('/terms'), type: 'website' }),
  component: TermsPage,
});

/**
 * 이용약관. OKKY 통합 약관의 구성을 참고하되, 이 서비스에 없는 것(포인트·유료
 * 서비스·광고성 정보 전송)은 넣지 않는다. 없는 걸 있다고 적는 약관은 독자를
 * 속이는 문서다.
 */
function TermsPage() {
  const S = LEGAL.service;

  return (
    <PageContainer>
      <PageHeader title="이용약관" description={`시행일 ${LEGAL.effectiveDate}`} />
      <LegalDoc>
        <Article title="제1조 (목적)">
          <p>
            이 약관은 {orBlank(LEGAL.operator)}(이하 「운영자」)가 {LEGAL.domain} 에서 제공하는 {S} 서비스(이하
            「서비스」)를 이용하는 데 필요한 운영자와 이용자의 권리·의무·책임 사항을 정합니다.
          </p>
        </Article>

        <Article title="제2조 (서비스의 내용)">
          <p>{S}는 다음을 제공합니다.</p>
          <List
            items={[
              '정부·지자체·공공기관이 공개한 중소기업 지원사업 공고를 모아 정리한 목록과 상세 정보(접수 기간, 지원 금액, 신청 자격 등)',
              '공고문에서 자동으로 추출·해석한 지원 내용 요약',
              '공고별 댓글, 설문·투표, 프로필, 글(「전지적 좋소 시점」) 등 이용자가 참여하는 기능',
              'AI 를 이용한 진단 등 부가 기능',
            ]}
          />
          <p>
            공고 정보는 각 기관이 공개한 원문을 바탕으로 하며, 지원사업의 신청·심사·선정은 해당 기관이
            합니다. {S}는 신청을 대행하거나 선정을 보장하지 않습니다.
          </p>
          <p>
            자동 추출·해석한 금액·자격 등은 원문과 다를 수 있습니다. 신청 전에 반드시 기관 원문을 확인해야
            하며, 운영자는 그 차이로 생긴 손해에 대해 책임지지 않습니다.
          </p>
        </Article>

        <Article title="제3조 (회원 가입과 계정)">
          <List
            items={[
              '공고 열람 등 대부분의 기능은 가입 없이 이용할 수 있습니다. 댓글·투표·프로필 등 참여 기능은 로그인이 필요합니다.',
              '가입은 카카오 계정으로 로그인하는 방식으로 이루어지며, 이용자는 닉네임 등 필요한 정보를 사실대로 입력해야 합니다.',
              '만 14세 미만은 가입할 수 없습니다.',
              '계정은 본인만 이용할 수 있으며 타인에게 양도·대여할 수 없습니다. 계정 관리 소홀로 생긴 결과는 이용자가 책임집니다.',
              '운영자는 허위 정보 가입, 타인 사칭, 이 약관 위반이 확인된 계정의 이용을 제한하거나 해지할 수 있습니다.',
            ]}
          />
        </Article>

        <Article title="제4조 (게시물의 권리와 책임)">
          <List
            items={[
              '이용자가 서비스에 올린 댓글·글·프로필 등 게시물의 저작권은 이용자에게 있습니다.',
              '이용자는 운영자에게 게시물을 서비스 안에서 저장·게시·검색·요약·복제·전송하는 데 필요한 범위의 무상 이용권을 부여합니다. 이 이용권은 서비스 운영과 홍보를 위해 필요한 범위로 한정됩니다.',
              '게시물의 내용에 대한 책임은 작성자에게 있습니다. 타인의 권리를 침해하거나 법령·이 약관을 위반하는 게시물은 사전 통지 없이 삭제되거나 노출이 제한될 수 있습니다.',
              '탈퇴하면 계정 정보는 삭제되지만, 다른 이용자와의 대화 맥락을 지키기 위해 댓글 등 일부 게시물은 작성자 표시를 지운 채 남을 수 있습니다. 삭제를 원하면 탈퇴 전에 직접 삭제하거나 운영자에게 요청할 수 있습니다.',
            ]}
          />
        </Article>

        <Article title="제5조 (금지 행위)">
          <p>이용자는 다음 행위를 해서는 안 됩니다.</p>
          <List
            items={[
              '허위 정보로 가입하거나 타인의 계정·정보를 도용하는 행위',
              '다른 이용자를 위협·괴롭히거나 명예를 훼손하는 게시물, 혐오·차별·음란한 게시물을 올리는 행위',
              '범죄와 관련되거나 법령에 어긋나는 게시물을 올리는 행위',
              '타인의 저작권·상표권 등 지적재산권을 침해하는 행위',
              '광고·스팸·영리 목적의 홍보물을 반복해서 올리는 행위',
              '자동화된 수단으로 서비스의 데이터를 대량 수집하거나 서비스를 방해하는 행위',
              '운영자의 허락 없이 서비스를 복제·재배포하거나 상업적으로 이용하는 행위',
            ]}
          />
        </Article>

        <Article title="제6조 (서비스의 변경과 중단)">
          <List
            items={[
              '운영자는 서비스의 기능·화면·데이터 구성을 언제든지 바꾸거나 추가·중단할 수 있습니다. 이용자에게 중요한 변경은 서비스 안 공지로 알립니다.',
              '점검, 장애, 외부 기관의 데이터 제공 중단, 그 밖의 불가피한 사유로 서비스 제공이 일시 중단될 수 있습니다.',
              '서비스는 무료로 제공되며, 운영자는 서비스의 계속 제공을 보장하지 않습니다.',
            ]}
          />
        </Article>

        <Article title="제7조 (개인정보 보호)">
          <p>
            운영자는 이용자의 개인정보를 개인정보처리방침과 관련 법령에 따라 보호합니다. 수집 항목·이용 목적·보관
            기간 등 자세한 내용은 개인정보처리방침(<a href="/privacy" className="underline">/privacy</a>)에서
            확인할 수 있습니다.
          </p>
        </Article>

        <Article title="제8조 (책임의 제한)">
          <List
            items={[
              '운영자는 서비스에 표시된 공고 정보의 정확성·완전성·최신성을 보증하지 않습니다. 공고 정보는 각 기관의 원문이 우선합니다.',
              '운영자는 이용자 사이의 다툼, 이용자와 지원사업 주관기관 사이의 다툼에 개입하지 않으며 책임지지 않습니다.',
              '천재지변, 외부 서비스 장애 등 운영자가 통제할 수 없는 사유, 이용자의 귀책 사유로 생긴 손해에 대해 운영자는 책임지지 않습니다.',
              '운영자의 고의 또는 중대한 과실로 이용자에게 손해가 생긴 경우 관련 법령에 따라 배상합니다.',
            ]}
          />
        </Article>

        <Article title="제9조 (계약 해지)">
          <List
            items={[
              '이용자는 언제든지 탈퇴할 수 있으며, 탈퇴 신청은 즉시 처리됩니다.',
              '운영자는 이용자가 이 약관을 위반한 경우 이용을 제한하거나 계약을 해지할 수 있습니다.',
              '탈퇴 뒤 다시 가입하는 경우 일부 기능의 이용이 제한될 수 있습니다.',
            ]}
          />
        </Article>

        <Article title="제10조 (약관의 게시와 개정)">
          <List
            items={[
              '이 약관은 서비스 화면에 게시합니다.',
              '운영자는 관련 법령을 어기지 않는 범위에서 약관을 개정할 수 있습니다. 개정하면 시행일과 개정 이유를 적어 시행일 7일 전부터 공지하며, 이용자에게 불리한 변경은 30일 전부터 공지합니다.',
              '이용자가 개정 약관에 동의하지 않으면 이용 계약을 해지할 수 있습니다. 공지 뒤에도 서비스를 계속 이용하면 개정 약관에 동의한 것으로 봅니다.',
            ]}
          />
        </Article>

        <Article title="제11조 (준거법과 관할)">
          <p>
            이 약관은 대한민국 법령에 따라 해석되며, 서비스 이용과 관련한 분쟁은 민사소송법이 정한 관할
            법원에서 다룹니다.
          </p>
        </Article>

        <Article title="부칙">
          <p>이 약관은 {LEGAL.effectiveDate}부터 시행합니다.</p>
          <p className="text-gray-500">문의: {orBlank(LEGAL.email)}</p>
        </Article>
      </LegalDoc>
    </PageContainer>
  );
}
